<template>
  <div class="user-login">
    <main-button
      class="register-btn"
      :text="$t('emailLogin') || 'PRIJAVA EMAILOM'"
      outline
      color="black"
      @click="showEmailLoginDialog = true"
    />

    <google-login @success="emitSuccess" @error="handleGoogleError" />
    <facebook-login />

    <simple-snackbar v-bind.sync="snackbar" />

    <!-- Email login modal -->
    <v-dialog v-model="showEmailLoginDialog" max-width="500">
      <v-card class="login-modal-card">
        <v-card-title style="padding: 24px 24px 0 24px; justify-content: space-between">
          <span class="login-title">
            {{
              otpMode
                ? $t('otpLoginTitle')
                : signupMode
                  ? $t('signupHeader', 'Start Your Free Trial.')
                  : $t('loginHere')
            }}
          </span>
          <v-icon style="cursor: pointer" @click="closeEmailLoginDialog">close</v-icon>
        </v-card-title>
        <v-card-text style="padding: 24px">
          <template v-if="!otpMode && !signupMode">
            <div class="login-desc">
              {{ $t('loginModalDescription') }}
            </div>
            <v-form
              style="width: 100%"
              @submit.prevent="submitForm"
              @keydown.enter.native="submitForm()"
            >
              <login-form class="inputs" :vs="$v" :initial-values="{}" @values="form = $event" />

              <div class="actions">
                <main-button
                  type="submit"
                  :text="$t('logIn')"
                  depressed
                  color="black"
                  style="width: 100%"
                />
                <main-button
                  flat
                  type="button"
                  :text="$t('forgotPasswordLogin')"
                  @click.prevent="goToForgotPassword"
                />
              </div>
            </v-form>

            <div class="separator-with-text">
              <div class="line" />
              <div class="text">{{ $t('noPasswordQuestion') }}</div>
              <div class="line" />
            </div>

            <div class="button-holder">
              <main-button :text="$t('oneTimePasswordLogin')" flat @click="goToOneTimePassword" />
            </div>

            <div class="signup-footer">
              <span>{{ $t('noAccountQuestion') || 'Nemaš račun?' }}</span>
              <a class="signup-link" @click.prevent="goToSignUp">{{ $t('signUpHere') }}</a>
            </div>
          </template>

          <template v-else-if="signupMode">
            <sign-up-form @submit="handleSignUpSubmit">
              <template #submit-button="{ submit }">
                <main-button
                  :text="$t('continue', 'Continue')"
                  depressed
                  color="black"
                  style="width: 100%"
                  @click="submit"
                />
              </template>
            </sign-up-form>

            <div class="link-holder">
              <a class="back-to-password-link" @click.prevent="backToPasswordLogin">
                {{ $t('backToPasswordLogin') }}
              </a>
            </div>
          </template>

          <template v-else>
            <div class="login-desc">
              {{ $t('otpLoginDescription') }}
            </div>

            <v-text-field
              v-model="otpEmail"
              :label="$t('enterEmail')"
              hide-details
              style="margin-top: 24px"
              box
            />

            <otp-code-input
              v-if="otpSent"
              ref="otp"
              v-model="otpCode"
              style="margin: 24px 0"
              :length="6"
              :autofocus="true"
            />

            <div class="actions">
              <main-button
                v-if="!otpSent"
                :text="$t('sendOneTimePassword')"
                depressed
                color="black"
                style="width: 100%"
                @click="sendOtp"
              />
              <main-button
                v-else
                :text="$t('logIn')"
                depressed
                color="black"
                style="width: 100%"
                @click="loginWithOtp"
              />
            </div>

            <div class="separator-with-text">
              <div class="line" />
              <div class="text">Ili</div>
              <div class="line" />
            </div>

            <div class="link-holder">
              <a class="back-to-password-link" @click.prevent="backToPasswordLogin">
                {{ $t('backToPasswordLogin') }}
              </a>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { email, required } from 'vuelidate/lib/validators'
import { submitGuard } from '@/utils'
import { LOGIN, SEND_OTP, LOGIN_WITH_OTP } from '@/graphql/auth.graphql'
import i18next from 'i18next'
import { signUp } from '@/http'
import GoogleLogin from '@/components/user/GoogleLogin.vue'
import FacebookLogin from '@/components/user/FacebookLogin.vue'
import LoginForm from '@/components/user/login-form.vue'
import SignUpForm from '@/components/user/sign-up-form.vue'
import OtpCodeInput from '@/components/user/OtpCodeInput.vue'
import MainButton from '@/components/shared/main-button.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  name: 'user-login',
  components: {
    GoogleLogin,
    FacebookLogin,
    LoginForm,
    SignUpForm,
    OtpCodeInput,
    MainButton,
    SimpleSnackbar,
  },
  data: () => ({
    form: {},
    snackbar: { open: false, messageProp: '', color: 'primary' },
    showEmailLoginDialog: false,
    otpMode: false,
    signupMode: false,
    otpSent: false,
    otpEmail: '',
    otpCode: '',
  }),
  watch: {
    showEmailLoginDialog(value) {
      if (!value) {
        this.backToPasswordLogin()
      }
    },
  },
  validations() {
    return {
      form: {
        email: { required, email },
        password: { required },
      },
    }
  },
  methods: {
    async submitForm() {
      await submitGuard(this, this.login)
    },
    async login() {
      const { data, errors } = await this.$apollo.mutate({
        mutation: LOGIN,
        variables: this.form,
      })
      if (errors) {
        this.snackbar = { open: true, messageProp: errors[0].message, color: 'error' }
      } else if (data && data.login) {
        this.emitSuccess()
      } else {
        this.snackbar = { open: true, messageProp: 'Wrong username or password', color: 'error' }
      }
    },
    emitSuccess() {
      this.$emit('success')
    },
    handleGoogleError(message) {
      this.snackbar = {
        open: true,
        messageProp: message || 'Google login failed. Please try again.',
        color: 'error',
      }
    },
    goToForgotPassword() {
      this.$router.push({ name: this.routes.shared.forgotPassword })
    },
    goToSignUp() {
      this.signupMode = true
      this.otpMode = false
    },
    goToOneTimePassword() {
      this.otpMode = true
      this.signupMode = false
      this.otpSent = false
      this.otpEmail = ''
      this.otpCode = ''
    },
    closeEmailLoginDialog() {
      this.backToPasswordLogin()
      this.showEmailLoginDialog = false
    },
    backToPasswordLogin() {
      this.otpMode = false
      this.signupMode = false
      this.otpSent = false
      this.otpEmail = ''
      this.otpCode = ''
    },
    async handleSignUpSubmit(formData) {
      try {
        const { errors: signUpErrors } = await signUp(formData)
        if (signUpErrors) {
          this.snackbar = {
            open: true,
            messageProp: signUpErrors[0].message,
            color: 'error',
          }

          return
        }

        const { data, errors: loginErrors } = await this.$apollo.mutate({
          mutation: LOGIN,
          variables: { email: formData.email, password: formData.password },
        })

        if (loginErrors) {
          this.snackbar = {
            open: true,
            messageProp: loginErrors[0].message,
            color: 'error',
          }

          return
        }

        if (data && data.login) {
          this.emitSuccess()
          this.closeEmailLoginDialog()
        } else {
          this.snackbar = {
            open: true,
            messageProp: 'Neuspjela prijava nakon registracije',
            color: 'error',
          }
        }
      } catch (e) {
        this.snackbar = {
          open: true,
          messageProp: e.message || 'Greška pri registraciji',
          color: 'error',
        }
      }
    },
    async sendOtp() {
      if (!this.otpEmail) {
        this.snackbar = { open: true, messageProp: 'Unesite email', color: 'error' }

        return
      }
      try {
        const lang = (i18next && (i18next.resolvedLanguage || i18next.language)) || 'hr'
        const { data, errors } = await this.$apollo.mutate({
          mutation: SEND_OTP,
          variables: { email: this.otpEmail, lang },
        })
        if (errors) throw new Error(errors[0].message)
        if (data && data.sendOtp) {
          this.otpSent = true
          this.$nextTick(() => this.$refs.otp && this.$refs.otp.focusFirst())
          this.snackbar = {
            open: true,
            messageProp: 'Poslali smo jednokratnu lozinku na vašu email adresu.',
            color: 'primary',
          }
        } else {
          throw new Error('Greška pri slanju koda')
        }
      } catch (e) {
        this.snackbar = { open: true, messageProp: e.message || 'Greška', color: 'error' }
      }
    },
    async loginWithOtp() {
      const code = this.otpCode
      if (code.length !== 6) {
        this.snackbar = { open: true, messageProp: 'Unesite 6-znamenkasti kod', color: 'error' }

        return
      }
      try {
        const isLegacyBrowser = /MSIE|Trident\//.test(navigator.userAgent)
        const { data, errors } = await this.$apollo.mutate({
          mutation: LOGIN_WITH_OTP,
          variables: { email: this.otpEmail, otp: code, isLegacyBrowser },
        })
        if (errors) throw new Error(errors[0].message)
        if (data && data.loginWithOtp) {
          this.emitSuccess()
          this.closeEmailLoginDialog()
        } else {
          throw new Error('Neuspjela prijava')
        }
      } catch (e) {
        this.snackbar = { open: true, messageProp: e.message || 'Greška', color: 'error' }
      }
    },
  },
}
</script>

<style scoped>
.user-login {
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.separator {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 520px;
  margin-top: 24px;
}

.separator.small {
  margin-top: 24px;
}

.line {
  flex: 1;
  border: 0.5px solid rgba(151, 151, 151, 0.5);
}

.text {
  margin: 0 16px;
  color: #333;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.inputs {
  width: 100%;
  margin-top: 24px;
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
}

.login-desc {
  font-family: 'Source Sans Pro';
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
}
/* OTP input styles moved to OtpCodeInput.vue */

.forgot-password {
  color: #0277bd;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.register {
  display: flex;
  justify-content: center;
  width: 100%;
}

.register-btn {
  text-transform: uppercase;
  margin: 0;
}

.tiny-separator {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 12px 0 16px 0;
}

.tiny-line {
  width: 160px;
  border: 0.5px solid rgba(151, 151, 151, 0.5);
}

.separator-with-text {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 16px;
}

.separator-with-text .line {
  flex: 1;
}

.separator-with-text .text {
  margin: 0 16px;
  color: #333;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 14px;
}

.button-holder {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.link-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.back-to-password-link {
  display: block;
  text-align: center;
  color: #0277bd;
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: none;
  line-height: 150%;
  cursor: pointer;
}

.signup-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 14px;
}

.signup-link {
  color: #0277bd;
  cursor: pointer;
  text-decoration: underline;
}

.login-title {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.72px;
}

.login-desc {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
@media screen and (max-width: 520px) {
  .actions {
    flex-direction: column;
  }
}
</style>
